import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as pdfjsLib from 'pdfjs-dist';

// Set up the worker from node_modules
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString();

interface PDFViewerProps {
  url: string;
  zoom: number;
  onZoomChange: (zoom: number) => void;
  onPageChange?: (current: number, total: number) => void;
}

const PDFViewer: React.FC<PDFViewerProps> = ({ url, zoom, onZoomChange, onPageChange }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [pdf, setPdf] = useState<pdfjsLib.PDFDocumentProxy | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const renderTaskRef = useRef<any>(null);
  
  // Touch gesture refs
  const lastTouchDistance = useRef<number | null>(null);

  // Track container size with ResizeObserver for reliable measurements
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateDimensions = () => {
      const rect = container.getBoundingClientRect();
      console.log('PDFViewer container dimensions:', rect.width, rect.height);
      if (rect.width > 0 && rect.height > 0) {
        setDimensions({ width: rect.width, height: rect.height });
      }
    };

    // Initial measurement - wait a bit for layout
    const timer = setTimeout(updateDimensions, 100);

    // Use ResizeObserver for accurate tracking
    const resizeObserver = new ResizeObserver(updateDimensions);
    resizeObserver.observe(container);

    // Also listen for orientation changes on mobile
    window.addEventListener('orientationchange', () => {
      setTimeout(updateDimensions, 200);
    });
    
    return () => {
      clearTimeout(timer);
      resizeObserver.disconnect();
      window.removeEventListener('orientationchange', updateDimensions);
    };
  }, []);

  // Load PDF document
  useEffect(() => {
    let cancelled = false;
    
    const loadPDF = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const loadingTask = pdfjsLib.getDocument(url);
        const pdfDoc = await loadingTask.promise;
        
        if (!cancelled) {
          setPdf(pdfDoc);
          setTotalPages(pdfDoc.numPages);
          setCurrentPage(1);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error loading PDF:', err);
          setError('Failed to load PDF');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadPDF();

    return () => {
      cancelled = true;
    };
  }, [url]);

  // Render current page
  useEffect(() => {
    const renderPage = async () => {
      if (!pdf || !canvasRef.current) return;

      // Cancel any existing render task
      if (renderTaskRef.current) {
        try {
          renderTaskRef.current.cancel();
        } catch (e) {
          // Ignore cancel errors
        }
      }

      try {
        const page = await pdf.getPage(currentPage);
        
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');
        if (!context) return;

        // Use actual viewport width or fallback
        const viewportWidth = dimensions.width > 0 ? dimensions.width : window.innerWidth;
        
        // Calculate scale to fit width
        const viewport = page.getViewport({ scale: 1 });
        const scale = ((viewportWidth - 40) / viewport.width) * zoom; // 40px for padding
        
        const scaledViewport = page.getViewport({ scale });

        // Set canvas dimensions with device pixel ratio for sharpness
        const pixelRatio = window.devicePixelRatio || 1;
        canvas.width = scaledViewport.width * pixelRatio;
        canvas.height = scaledViewport.height * pixelRatio;
        canvas.style.width = `${scaledViewport.width}px`;
        canvas.style.height = `${scaledViewport.height}px`;
        
        context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);

        // Render PDF page
        const renderTask = page.render({
          canvasContext: context,
          viewport: scaledViewport,
        });
        
        renderTaskRef.current = renderTask;
        await renderTask.promise;

      } catch (err: any) {
        if (err?.name !== 'RenderingCancelledException') {
          console.error('Error rendering page:', err);
        }
      }
    };

    renderPage();
  }, [pdf, currentPage, zoom, dimensions]);

  // Touch handlers for pinch zoom
  const getTouchDistance = (touches: React.TouchList): number => {
    if (touches.length < 2) return 0;
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      lastTouchDistance.current = getTouchDistance(e.touches);
    }
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2 && lastTouchDistance.current !== null) {
      e.preventDefault();
      const currentDistance = getTouchDistance(e.touches);
      const delta = currentDistance - lastTouchDistance.current;
      
      if (Math.abs(delta) > 5) {
        const zoomDelta = delta * 0.005;
        const newZoom = Math.max(0.5, Math.min(3, zoom + zoomDelta));
        onZoomChange(newZoom);
        lastTouchDistance.current = currentDistance;
      }
    }
  }, [zoom, onZoomChange]);

  const handleTouchEnd = useCallback(() => {
    lastTouchDistance.current = null;
  }, []);

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      onPageChange?.(page, totalPages);
    }
  };

  const goToPrevPage = () => goToPage(currentPage - 1);
  const goToNextPage = () => goToPage(currentPage + 1);

  if (loading) {
    return (
      <div className="w-full h-full flex items-center justify-center" style={{ minHeight: '100%' }}>
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p>Loading PDF...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full h-full flex items-center justify-center" style={{ minHeight: '100%' }}>
        <div className="text-center text-white p-8">
          <svg className="w-16 h-16 mx-auto mb-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <p className="text-lg font-medium mb-2">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className="w-full h-full overflow-auto flex flex-col"
      style={{ 
        backgroundColor: '#525659',
        WebkitOverflowScrolling: 'touch',
        minHeight: '100%',
        touchAction: 'pan-x pan-y pinch-zoom'
      }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Page navigation - only show if multi-page */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4 py-2 bg-gray-800/80 backdrop-blur sticky top-0 z-10">
          <button
            onClick={goToPrevPage}
            disabled={currentPage <= 1}
            className="p-2 text-white hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <span className="text-white text-sm font-medium">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={goToNextPage}
            disabled={currentPage >= totalPages}
            className="p-2 text-white hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      )}

      {/* PDF Canvas - centered with pan support */}
      <div className="flex-1 flex justify-center p-4 overflow-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
        <canvas 
          ref={canvasRef}
          className="shadow-2xl"
          style={{ 
            maxWidth: zoom <= 1 ? '100%' : 'none',
            backgroundColor: 'white',
            display: 'block',
            touchAction: 'pan-x pan-y'
          }}
        />
      </div>
    </div>
  );
};

export default PDFViewer;
